import { Box, Paper } from "@mui/material"
import { FilterAltOutlined } from "@mui-symbols-material/w300"
import { useNavigate, useSearch } from "@tanstack/react-router"
import type { OnChangeFn, SortingState } from "@tanstack/react-table"
import type { FC } from "react"
import { useEffect, useMemo, useRef, useState } from "react"

import { Button } from "@/components/common/Button"
import { PageHeader } from "@/components/PageHeader/PageHeader"
import { TableHeaderActions } from "@/components/Tables/TableHeaderActions"
import type { ChangeLogSortField } from "@/graphql/codegen/graphql"
import { SortingOrder } from "@/graphql/codegen/graphql"
import { useScrollLoadMore } from "@/hooks/useScrollLoadMore"
import { useThrottle } from "@/hooks/useThrottle"
import { queryClient } from "@/providers/GraphqlRouterProvider"
import ChangeLogTable from "@/screens/ChangeLog/components/ChangeLogTable"
import ChangeLogFilterChips from "@/screens/ChangeLog/components/filter/ChangeLogFilterChips"
import { FilterDrawer } from "@/screens/ChangeLog/components/filter/FilterDrawer"
import { CHANGE_LOG_SEARCH_PLACEHOLDER } from "@/screens/ChangeLog/constants"
import type { ChangeLogSortableAccessorKey } from "@/screens/ChangeLog/constants/changeLogSorting"
import {
  accessorKeyToChangeLogSortField,
  changeLogSortFieldToAccessoryKey,
} from "@/screens/ChangeLog/constants/changeLogSorting"
import type { ChangeLogSearchFilters } from "@/screens/ChangeLog/stores/useChangeLogSearchStore"
import { useChangeLogSearchStore } from "@/screens/ChangeLog/stores/useChangeLogSearchStore"

const ChangeLogContainer: FC = () => {
  const navigate = useNavigate()
  const currentSearch = useSearch({ from: "/app/admin/change-log/" })

  const [filterDrawerOpen, setFilterDrawerOpen] = useState<boolean>(false)

  const { field, order, query, categories, subcategories, start, end, userIds } = currentSearch

  const data = useChangeLogSearchStore(({ data }) => data)
  const fetching = useChangeLogSearchStore(({ fetching }) => fetching)
  const error = useChangeLogSearchStore(({ error }) => error)
  const executeSearch = useChangeLogSearchStore(({ executeSearch }) => executeSearch)
  const loadMore = useChangeLogSearchStore(({ loadMore }) => loadMore)
  const clearStore = useChangeLogSearchStore(({ clearStore }) => clearStore)

  const tableContainerRef = useRef<HTMLDivElement>(null)

  const sorting = useMemo(() => {
    if (!field) return []

    const tableSortField = changeLogSortFieldToAccessoryKey.get(field)
    if (!tableSortField) return []

    return [
      {
        id: tableSortField,
        desc: order === SortingOrder.Desc,
      },
    ]
  }, [field, order])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearStore()
    }
  }, [clearStore])

  useEffect(() => {
    const searchFilters: ChangeLogSearchFilters = {
      field,
      order,
      query,
      categories,
      subcategories,
      start,
      end,
      userIds,
    }
    executeSearch(queryClient, searchFilters)
  }, [field, order, query, categories, subcategories, executeSearch, start, end, userIds])

  const [throttledLoadMore] = useThrottle(() => {
    const searchFilters: ChangeLogSearchFilters = {
      field,
      order,
      query,
      categories,
      subcategories,
      start,
      end,
      userIds,
    }
    loadMore(queryClient, searchFilters)
  }, 200)

  const handleScroll = useScrollLoadMore({
    fetching,
    loadMore: throttledLoadMore,
    scrollContainerRef: tableContainerRef,
  })

  const handleSortingChange: OnChangeFn<SortingState> = (updaterFn) => {
    // @ts-expect-error - updaterFn is know fn
    const newSorting = updaterFn(sorting)
    if (newSorting.length) {
      const sort = newSorting[0]
      const key = sort.id as ChangeLogSortableAccessorKey
      const field = accessorKeyToChangeLogSortField.get(key) as ChangeLogSortField
      const params = {
        field,
        order: sort.desc ? SortingOrder.Desc : SortingOrder.Asc,
      }

      navigate({
        search: params,
        to: "/app/admin/change-log",
        replace: true,
      })
    }
  }

  return (
    <Paper elevation={0} className='flex min-h-0 grow flex-col gap-4 overflow-hidden bg-white'>
      <FilterDrawer showFilterDrawer={filterDrawerOpen} toggleDrawer={setFilterDrawerOpen} />

      <PageHeader title='Change Log' />

      <TableHeaderActions currentSearch={currentSearch} placeholder={CHANGE_LOG_SEARCH_PLACEHOLDER}>
        <Button
          appearance='outlined'
          value='Filter'
          startIcon={<FilterAltOutlined />}
          onClick={() => setFilterDrawerOpen(true)}
          rootElementName='button'
        />
      </TableHeaderActions>

      <ChangeLogFilterChips />

      <Box className='flex flex-1 flex-col overflow-y-auto'>
        <ChangeLogTable
          data={data}
          tableContainerRef={tableContainerRef}
          onSortingChange={handleSortingChange}
          sorting={sorting}
          scrollHandler={handleScroll}
          fetching={fetching}
          error={error}
        />
      </Box>
    </Paper>
  )
}

export default ChangeLogContainer
